import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ImagePlus, Trash2, Loader2, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useHospitalProfileStore } from "@/lib/stores/hospitalProfile";
import { useWallpaperStore } from "@/lib/stores/wallpaper";
import { Badge } from "@/components/ui/badge";

export function WallpaperUpload() {
  const { toast } = useToast();
  const { 
    profile, 
    isLoading, 
    uploadWallpaper, 
    updateWallpaper, 
    removeWallpaper 
  } = useHospitalProfileStore();
  const { setWallpaper, clearWallpaper } = useWallpaperStore();
  
  const [preview, setPreview] = useState<string>(profile.wallpaperUrl || "");
  const [opacity, setOpacity] = useState<number>(profile.wallpaperOpacity || 0.2);
  const [isUploading, setIsUploading] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync wallpaper store when profile changes
  useEffect(() => {
    if (profile.wallpaperUrl) {
      setWallpaper(profile.wallpaperUrl, profile.wallpaperOpacity || 0.2);
      setPreview(profile.wallpaperUrl);
      setOpacity(profile.wallpaperOpacity || 0.2);
    }
  }, [profile.wallpaperUrl, profile.wallpaperOpacity, setWallpaper]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 10MB",
        variant: "destructive"
      });
      return;
    }
    
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please select a JPEG, PNG, or WebP image",
        variant: "destructive"
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const wallpaperUrl = await uploadWallpaper(file);
      await updateWallpaper(wallpaperUrl, opacity);
      
      // Update the wallpaper store immediately
      setWallpaper(wallpaperUrl, opacity);
      
      // Also update the preview
      setPreview(wallpaperUrl);
      
      toast({
        title: "Wallpaper uploaded",
        description: "Your wallpaper has been updated successfully"
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload wallpaper",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpacityChange = async (value: number[]) => {
    const newOpacity = value[0];
    setOpacity(newOpacity);
    
    if (profile.wallpaperUrl) {
      try {
        await updateWallpaper(profile.wallpaperUrl, newOpacity);
        setWallpaper(profile.wallpaperUrl, newOpacity);
        toast({
          title: "Opacity updated",
          description: "Wallpaper opacity has been updated"
        });
      } catch (error) {
        toast({
          title: "Update failed",
          description: "Failed to update wallpaper opacity",
          variant: "destructive"
        });
      }
    }
  };

  const handleRemove = async () => {
    try {
      await removeWallpaper();
      clearWallpaper();
      setPreview("");
      setOpacity(0.2);
      toast({
        title: "Wallpaper removed",
        description: "Wallpaper has been removed successfully"
      });
    } catch (error) {
      toast({
        title: "Remove failed",
        description: "Failed to remove wallpaper",
        variant: "destructive"
      });
    }
  };

  const togglePreview = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImagePlus className="h-5 w-5" />
          Wallpaper Settings
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Upload a custom wallpaper that will be displayed across all dashboards
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Wallpaper Preview */}
        {preview && (
          <div className="space-y-2">
            <Label>Current Wallpaper</Label>
            <div className="relative group">
              <div 
                className="w-full h-32 rounded-lg border-2 border-dashed border-gray-300 overflow-hidden relative"
                style={{
                  backgroundImage: `linear-gradient(rgba(255, 255, 255, ${opacity}), rgba(255, 255, 255, ${opacity})), url(${preview})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                  <Badge variant="secondary" className="opacity-0 group-hover:opacity-100 transition-opacity">
                    Preview
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File Upload */}
        <div className="space-y-2">
          <Label>Upload New Wallpaper</Label>
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <ImagePlus className="h-4 w-4 mr-2" />
              Choose File
            </Button>
            {fileInputRef.current?.files?.[0] && (
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  'Upload Wallpaper'
                )}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Supported formats: JPEG, PNG, WebP. Max size: 10MB
          </p>
        </div>

        {/* Opacity Control */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Wallpaper Opacity</Label>
            <Badge variant="outline">{Math.round(opacity * 100)}%</Badge>
          </div>
          <Slider
            value={[opacity]}
            onValueChange={handleOpacityChange}
            max={1}
            min={0}
            step={0.05}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Transparent</span>
            <span>Opaque</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={togglePreview}
              disabled={!preview}
            >
              <Eye className="h-4 w-4 mr-2" />
              {isPreviewMode ? 'Hide Preview' : 'Show Preview'}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {profile.wallpaperUrl && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleRemove}
                disabled={isLoading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove Wallpaper
              </Button>
            )}
          </div>
        </div>

        {/* Preview Mode */}
        {isPreviewMode && preview && (
          <div className="mt-4 p-4 border rounded-lg bg-muted/50">
            <Label className="text-sm font-medium">Live Preview</Label>
            <div 
              className="mt-2 w-full h-24 rounded border"
              style={{
                backgroundImage: `linear-gradient(rgba(255, 255, 255, ${opacity}), rgba(255, 255, 255, ${opacity})), url(${preview})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <p className="text-xs text-muted-foreground mt-2">
              This is how your wallpaper will appear across all dashboards
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
